import { Badge, Card, Group, SimpleGrid, Stack, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { type CashPair, MoneyPair, OverShortPair } from '../../components/CashFigures';
import { enumColor, enumLabel } from '../../lib/enums';
import { formatDateTime, formatMoney } from '../../lib/format';

/**
 * The cash story: the days he picked, told back one container at a time.
 *
 * Every figure on these cards exists somewhere else on this page already — a count on its sheet, a
 * drop on the safe's ledger, a deposit in the bank table. What did not exist was the reading-back:
 * "what did we think was in it, what was counted, what is the difference, and what moved" as one
 * short account per container, which is the question the owner actually opens this page to ask.
 * Without it he answers by holding five screens' figures in his head, and the held-in-the-head step
 * is where a small disagreement becomes "close enough".
 *
 * These cards SUMMARIZE and never replace: a figure that looks wrong is taken apart on the ledger
 * and the count sheets, which stay one click away. Nothing here is interactive on purpose — a
 * statement with buttons in it becomes a screen, and this page has those already.
 */

interface StoryCount {
  cashCountId: string;
  countedAt: string;
  byName: string | null;
  expected: CashPair | null;
  counted: CashPair;
  variance: CashPair | null;
}

interface StoryMovement {
  kind: 'in' | 'dropIn' | 'paidIn' | 'out' | 'dropOut' | 'paidOut' | 'banked';
  times: number;
  total: CashPair;
}

interface StoryPouches {
  dropped: number;
  checked: number;
  disagreeing: number;
  foundLessCounted: CashPair;
}

interface StoryEntry {
  cashLocationId: string;
  name: string;
  kind: 'drawer' | 'cajaita' | 'safe';
  lastCount: StoryCount | null;
  countsTaken: number;
  countVariance: CashPair;
  movements: StoryMovement[];
  pouches: StoryPouches | null;
}

interface Story {
  from: string;
  to: string;
  containers: StoryEntry[];
}

const isZero = (pair: CashPair) => pair.usdMinor === 0 && pair.hnlMinor === 0;

/** "3 drops" — the count is the headline, so the noun has to agree with it. */
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * "$ 150.00 · L 2,000.00" as plain text for the middle of a sentence. The `MoneyPair` component
 * draws the same thing as its own row, which is right for a figure and wrong inside prose.
 */
const pairInWords = (pair: CashPair) =>
  `${formatMoney(pair.usdMinor, 'USD')} · ${formatMoney(pair.hnlMinor, 'HNL')}`;

function StoryCard({ entry }: { entry: StoryEntry }) {
  const count = entry.lastCount;
  return (
    <Card withBorder>
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Text fw={600} size="sm">
          {entry.name}
        </Text>
        <Badge variant="light" color="gray">
          {enumLabel('cashBoxKind', entry.kind)}
        </Badge>
      </Group>

      {/* WHAT WE THOUGHT, WHAT WAS COUNTED, AND THE DIFFERENCE — the story's three figures, off the
          newest count in the range. A container nobody counted says so plainly: silence would read
          as "nothing to report", and not-looked-at is the opposite of nothing-to-report. */}
      {count ? (
        <Stack gap={2} mt="xs">
          <Text size="xs" c="dimmed">
            Last counted {formatDateTime(count.countedAt)}
            {count.byName ? ` by ${count.byName}` : ''}
          </Text>
          {count.expected ? (
            <Group gap="xs" wrap="wrap">
              <Text size="sm">We thought {pairInWords(count.expected)};</Text>
              <Text size="sm">counted {pairInWords(count.counted)} —</Text>
              <OverShortPair pair={count.variance} />
            </Group>
          ) : (
            <Text size="sm">
              Counted {pairInWords(count.counted)}. There was nothing on record to measure it against, so this
              count is the starting point.
            </Text>
          )}
          {entry.countsTaken > 1 && (
            <Group gap={6} wrap="nowrap">
              <Text size="xs" c="dimmed">
                {plural(entry.countsTaken, 'count', 'counts')} in these days; together they came out
              </Text>
              {isZero(entry.countVariance) ? (
                <Text size="xs">exact</Text>
              ) : (
                <Text size="xs" fw={600}>
                  {pairInWords(entry.countVariance)}{' '}
                  {entry.countVariance.usdMinor < 0 || entry.countVariance.hnlMinor < 0 ? 'short' : 'over'}
                </Text>
              )}
            </Group>
          )}
        </Stack>
      ) : (
        <Text size="sm" c="dimmed" mt="xs">
          Not counted in these days.
        </Text>
      )}

      {/* The movements that got it there — summed per kind, one line each. "Nothing moved" is a
          real sentence rather than an empty gap, because a safe nothing moved through is a very
          different fact from a safe nobody looked at. */}
      <Stack gap={2} mt="sm">
        {entry.movements.length === 0 ? (
          <Text size="xs" c="dimmed">
            Nothing moved in or out.
          </Text>
        ) : (
          entry.movements.map((m) => (
            <Group key={m.kind} gap={6} justify="space-between" wrap="nowrap">
              <Text size="xs" c={enumColor('cashStoryMovement', m.kind)}>
                {enumLabel('cashStoryMovement', m.kind)} ({m.times}×)
              </Text>
              <MoneyPair pair={m.total} />
            </Group>
          ))
        )}
      </Stack>

      {/* THE POUCHES, safe only — the single most useful number on this card. A pouch counted into
          the bag as one figure at the register and out of it as another in the office is the most
          direct evidence of a hand in the cash this system can produce, so it gets its own
          sentence and is never folded into the movement totals above. */}
      {entry.pouches && entry.pouches.dropped > 0 && (
        <Stack gap={2} mt="sm">
          <Text size="xs" c="dimmed">
            {plural(entry.pouches.dropped, 'pouch dropped in', 'pouches dropped in')} —{' '}
            {entry.pouches.checked === entry.pouches.dropped
              ? 'all opened and checked'
              : `${entry.pouches.checked} opened and checked, ${
                  entry.pouches.dropped - entry.pouches.checked
                } not opened yet`}
          </Text>
          {entry.pouches.checked > 0 &&
            (entry.pouches.disagreeing === 0 ? (
              <Text size="xs">Every checked pouch matched the drawer it came from.</Text>
            ) : (
              <Text size="xs" c="red" fw={600}>
                {plural(entry.pouches.disagreeing, 'pouch', 'pouches')} did not match the drawer it came from
                — {pairInWords(entry.pouches.foundLessCounted)}{' '}
                {entry.pouches.foundLessCounted.usdMinor < 0 || entry.pouches.foundLessCounted.hnlMinor < 0
                  ? 'less came out than went in'
                  : 'more came out than went in'}
              </Text>
            ))}
        </Stack>
      )}
    </Card>
  );
}

export function TheCashStory({ from, to }: { from: string; to: string }) {
  const story = useQuery({
    queryKey: listKey('/admin/v1/cash/story', { from, to }),
    queryFn: () =>
      rawApi<Story>(
        'GET',
        `/admin/v1/cash/story?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ),
    enabled: Boolean(from && to),
  });

  if (story.isLoading) {
    return (
      <Text size="sm" c="dimmed">
        Loading…
      </Text>
    );
  }
  const containers = story.data?.containers ?? [];
  if (containers.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No cash box has been set up yet.
      </Text>
    );
  }
  return (
    <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
      {containers.map((entry) => (
        <StoryCard key={entry.cashLocationId} entry={entry} />
      ))}
    </SimpleGrid>
  );
}
